import React, { useState, useEffect, useCallback } from "react";
import {
  Tag,
  Spin,
  Empty,
  message,
  Typography,
  Card,
  Statistic,
  Row,
  Col,
  Table,
  Badge,
  Tooltip,
} from "antd";
import {
  RightOutlined,
  DownOutlined,
  UserAddOutlined,
  LinkOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import axios from "axios";
import styled from "styled-components";
import { API_CONST } from "../../../const";

const { Text, Title } = Typography;

const PageWrapper = styled.div`
  padding: 0;
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 4px 0; }
  p.subtitle { color: #666; font-size: 14px; margin: 0 0 24px 0; }
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const AccordionList = styled.div`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
`;

const AccordionRow = styled.div`
  border-bottom: 1px solid #f0f0f0;
  &:last-child { border-bottom: none; }
`;

const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 20px;
  cursor: pointer;
  background: ${({ expanded }) => (expanded ? "#f6f9fc" : "#fff")};
  transition: background 0.15s;
  gap: 14px;
  &:hover { background: #f6f9fc; }
`;

const ExpandIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: #f0f0f0;
  cursor: pointer;
  font-size: 10px;
  color: #555;
  flex-shrink: 0;
  transition: background 0.15s, transform 0.15s;
  &:hover { background: #d9d9d9; }
`;

const PartnerName = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: #014c75;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #666;
  white-space: nowrap;
`;

const AccordionBody = styled.div`
  padding: 0 24px 20px 54px;
  background: #fafcff;
  border-top: 1px solid #e8f0f8;
`;

const CustomerTable = styled.div`
  .ant-table-wrapper { border-radius: 6px; overflow: hidden; }
`;

const customerColumns = [
  {
    title: "Customer Org",
    dataIndex: "customerOrganization",
    key: "customerOrganization",
    width: 220,
    render: (v) => <Text strong style={{ color: "#014c75" }}>{v}</Text>,
  },
  {
    title: "Contact Name",
    dataIndex: "contactName",
    key: "contactName",
    width: 160,
  },
  {
    title: "Contact Email",
    dataIndex: "contactEmail",
    key: "contactEmail",
    width: 230,
    render: (v) => (
      <a href={`mailto:${v}`} style={{ color: "#1677ff" }}>{v}</a>
    ),
  },
  {
    title: "Date Joined",
    dataIndex: "dateJoined",
    key: "dateJoined",
    width: 130,
    render: (v) =>
      v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—",
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 110,
    render: (v) => {
      const colorMap = { active: "green", inactive: "default", pending: "orange" };
      return <Tag color={colorMap[v?.toLowerCase()] || "default"}>{v || "—"}</Tag>;
    },
  },
];

const PartnerAccordionItem = ({ partner }) => {
  const [expanded, setExpanded] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadCustomers = useCallback(async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(
        `${API_CONST.REFERRAL_BY_PARTNER}/${encodeURIComponent(partner.partnerOrgId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomers(res.data.customers || []);
      setLoaded(true);
    } catch (err) {
      message.error("Failed to load referred customers for " + partner.partnerOrgName);
    } finally {
      setLoading(false);
    }
  }, [partner.partnerOrgId, partner.partnerOrgName, loaded]);

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !loaded) {
      loadCustomers();
    }
  };

  return (
    <AccordionRow>
      <AccordionHeader expanded={expanded} onClick={handleToggle}>
        <ExpandIcon>
          {expanded ? <DownOutlined /> : <RightOutlined />}
        </ExpandIcon>

        <PartnerName>{partner.partnerOrgName}</PartnerName>

        <MetaItem>
          <LinkOutlined style={{ color: "#8c8c8c" }} />
          {partner.referralCode ? (
            <Tooltip title="Referral Code">
              <Tag
                style={{ fontFamily: "monospace", letterSpacing: 1, marginRight: 0 }}
                color="blue"
              >
                {partner.referralCode}
              </Tag>
            </Tooltip>
          ) : (
            <Tag color="default" style={{ marginRight: 0 }}>No Code</Tag>
          )}
        </MetaItem>

        <MetaItem>
          <TeamOutlined style={{ color: "#8c8c8c" }} />
          <Badge
            count={partner.referredCount}
            showZero
            style={{
              backgroundColor: partner.referredCount > 0 ? "#52c41a" : "#d9d9d9",
              fontSize: 11,
            }}
          />
          <span style={{ marginLeft: 4, color: "#888", fontSize: 12 }}>
            referred {partner.referredCount === 1 ? "customer" : "customers"}
          </span>
        </MetaItem>
      </AccordionHeader>

      {expanded && (
        <AccordionBody>
          {loading ? (
            <div style={{ padding: "20px 0", textAlign: "center" }}>
              <Spin size="small" tip="Loading customers..." />
            </div>
          ) : customers.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No customers referred via this partner's link yet."
              style={{ margin: "20px 0" }}
            />
          ) : (
            <CustomerTable style={{ marginTop: 16 }}>
              <Table
                dataSource={customers.map((c, i) => ({ ...c, key: c.customerOrganization + i }))}
                columns={customerColumns}
                size="small"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showTotal: (total) => `Total ${total} rows`,
                }}
                scroll={{ x: 860 }}
                bordered={false}
              />
            </CustomerTable>
          )}
        </AccordionBody>
      )}
    </AccordionRow>
  );
};

const AdminReferrals = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const res = await axios.get(API_CONST.REFERRAL_ALL_PARTNERS, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPartners(res.data.partners || []);
      } catch (err) {
        message.error("Failed to load referral data");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const totalReferred = partners.reduce((sum, p) => sum + (p.referredCount || 0), 0);
  const partnersWithReferrals = partners.filter((p) => p.referredCount > 0).length;
  const partnersWithCode = partners.filter((p) => p.referralCode).length;

  return (
    <PageWrapper>
      <h1>Referrals</h1>
      <p className="subtitle">
        Customers who signed up via a Partner's referral link
      </p>

      <SummaryRow>
        <Card size="small" style={{ minWidth: 180, borderRadius: 8 }}>
          <Statistic
            title="Total Partners"
            value={partners.length}
            prefix={<TeamOutlined style={{ color: "#014c75" }} />}
            valueStyle={{ color: "#014c75" }}
          />
        </Card>
        <Card size="small" style={{ minWidth: 200, borderRadius: 8 }}>
          <Statistic
            title="Partners with Referral Code"
            value={partnersWithCode}
            prefix={<LinkOutlined style={{ color: "#1677ff" }} />}
            valueStyle={{ color: "#1677ff" }}
          />
        </Card>
        <Card size="small" style={{ minWidth: 200, borderRadius: 8 }}>
          <Statistic
            title="Partners with Referrals"
            value={partnersWithReferrals}
            prefix={<UserAddOutlined style={{ color: "#52c41a" }} />}
            valueStyle={{ color: "#52c41a" }}
          />
        </Card>
        <Card size="small" style={{ minWidth: 200, borderRadius: 8 }}>
          <Statistic
            title="Total Referred Customers"
            value={totalReferred}
            prefix={<TeamOutlined style={{ color: "#fa8c16" }} />}
            valueStyle={{ color: "#fa8c16" }}
          />
        </Card>
      </SummaryRow>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" tip="Loading referral data..." />
        </div>
      ) : partners.length === 0 ? (
        <Empty description="No partner organisations found." style={{ padding: 60 }} />
      ) : (
        <>
          <div style={{ marginBottom: 10, fontSize: 13, color: "#888" }}>
            {partners.length} partner{partners.length !== 1 ? "s" : ""} — click any row to expand and see referred customer details
          </div>
          <AccordionList>
            {partners.map((p) => (
              <PartnerAccordionItem key={p.partnerOrgId} partner={p} />
            ))}
          </AccordionList>
        </>
      )}
    </PageWrapper>
  );
};

export default AdminReferrals;
